<!--
  DesignVersionRail.vue -- the version list in the design's left rail.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  Locked Decision 4 (rail), and the deliberate NON-implementation of
  Locked Decisions 5 and 6.

  The capability claim this component does not make
  =================================================
  Locked Decision 5 asks each entry to state provenance AND capability,
  with unsupported methods struck through; Locked Decision 6 asks for a
  capability line under the design header. Neither is built here, and
  tests bind the absence.

  ADR 0068 wants a declared vector / raster / stitch KIND on the
  version. **That column does not exist.** The read model says so
  itself at api/src/services/decoration_family_view.py:370-385 and
  ships the digitization record in its place. The only way to render a
  capability badge today would be to infer one from digitization
  fields -- which is a guess, and a guess rendered as a fact on the
  surface whose whole purpose is telling an operator what is actually
  true about their design. A struck-through "DTF" that is merely
  inferred would send someone to buy source art they may not need, or
  reassure them about a method that will fail at bind time.

  So the rail carries what is real per entry: the version number,
  when it was created, its digitization provenance, and how many sizes
  sit on it (`size_count`). When the declared kind lands, the badges
  slot in beside the provenance line without changing this shape.

  An estimate is always labelled as one. `estimated_stitch_count` and
  `digitized_stitch_count` are different facts, and stitch count feeds
  pricing -- presenting an estimate as a measured figure is the quiet
  kind of fiction this page exists to remove.
-->
<template>
    <div class="dvr" data-testid="design-version-rail">
        <div class="dvr__eyebrow">Versions</div>

        <div
            v-if="!versions.length"
            class="dvr__empty"
            data-testid="version-rail-empty"
        >No versions on this design yet.</div>

        <ul v-else class="dvr__list">
            <li
                v-for="version in versions"
                :key="version.id"
                class="dvr__entry"
                :class="{ 'dvr__entry--current': isCurrent(version) }"
                :data-testid="`version-rail-entry-${version.id}`"
            >
                <div class="dvr__top">
                    <span class="dvr__num">v{{ version.version_number }}</span>
                    <span
                        v-if="isCurrent(version)"
                        class="dvr__current"
                        :data-testid="`version-rail-current-${version.id}`"
                    >Current</span>
                    <span class="dvr__spacer" />
                    <span class="dvr__date">{{ dateOf(version) }}</span>
                </div>

                <div
                    class="dvr__provenance"
                    :data-testid="`version-rail-provenance-${version.id}`"
                >{{ provenanceOf(version) }}</div>

                <div
                    class="dvr__sizes"
                    :data-testid="`version-rail-sizes-${version.id}`"
                >{{ sizeCountOf(version) }}</div>
            </li>
        </ul>
    </div>
</template>

<script>
function formatCount(n) {
    return Number(n).toLocaleString();
}

export default {
    name: "DesignVersionRail",
    props: {
        /** The whole family payload. */
        family: {
            type: Object,
            required: true,
        },
    },
    computed: {
        versions() {
            const list = this.family?.versions;
            return Array.isArray(list) ? list : [];
        },
    },
    methods: {
        isCurrent(version) {
            const current = this.family?.current_version_id;
            if (current == null) return false;
            return Number(current) === Number(version.id);
        },
        dateOf(version) {
            const raw = version?.created_at;
            if (!raw) return "";
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return "";
            return d.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },
        /**
         * The provenance line -- the digitization record, which is the
         * durable per-version fact today. Never a capability claim.
         */
        provenanceOf(version) {
            const prov = version?.provenance ?? {};
            if (prov.digitized_stitch_count != null) {
                return `Digitized -- ${formatCount(
                    prov.digitized_stitch_count
                )} stitches`;
            }
            if (prov.digitized_file_url) {
                // Digitized, but the count did not come back with it.
                // Say the part that is known and no more.
                return "Digitized";
            }
            if (prov.estimated_stitch_count != null && prov.stitch_count_final) {
                // The upload IS the stitch file: its count is final for
                // pricing (#4305), and there is nothing left to digitize.
                return `Stitch file -- ${formatCount(
                    prov.estimated_stitch_count
                )} stitches`;
            }
            if (prov.estimated_stitch_count != null) {
                return `Not digitized yet -- est. ${formatCount(
                    prov.estimated_stitch_count
                )} stitches`;
            }
            return "Not digitized yet";
        },
        sizeCountOf(version) {
            const n = Number(version?.size_count ?? 0);
            if (!n) return "No sizes on this version";
            return `${n} size${n === 1 ? "" : "s"} on this version`;
        },
    },
};
</script>

<style scoped>
.dvr__eyebrow {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--tf-fg-4);
    margin-bottom: 8px;
}
.dvr__empty {
    padding: 12px;
    background: var(--tf-surface-alt);
    border: 1px dashed var(--tf-border-strong);
    border-radius: var(--tf-radius-sm);
    font-size: 12.5px;
    color: var(--tf-fg-3);
}
.dvr__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.dvr__entry {
    border: 1px solid var(--tf-border);
    background: var(--tf-surface);
    border-radius: var(--tf-radius-sm);
    padding: 9px 11px;
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.dvr__entry--current {
    border-color: var(--tf-primary);
    background: var(--tf-primary-soft);
}
.dvr__top {
    display: flex;
    align-items: center;
    gap: 7px;
}
.dvr__num {
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-fg-2);
}
.dvr__entry--current .dvr__num {
    color: var(--tf-primary);
}
.dvr__current {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--tf-primary);
    background: var(--tf-surface);
    border-radius: var(--tf-radius-pill);
    padding: 1px 7px;
}
.dvr__spacer {
    flex: 1;
}
.dvr__date {
    font-size: 11px;
    color: var(--tf-fg-4);
}
.dvr__provenance {
    font-size: 11.5px;
    color: var(--tf-fg-3);
}
.dvr__sizes {
    font-size: 11px;
    color: var(--tf-fg-4);
}
</style>
